import "./App.css";
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import MusicPlayer from './components/MusicPlayer';
import WebAuthGuard from './components/WebAuthGuard';
import ProtectedSectionGuard from './components/ProtectedSectionGuard';
import Home from './pages/Home';
import IntroGate from './components/IntroGate';
import useFonts from './hooks/useFonts';

// Route-level code splitting: secondary pages load on demand,
// keeping the initial bundle (home + intro) as small as possible.
const Confirmacion = lazy(() => import('./pages/Confirmacion'));
const Galeria      = lazy(() => import('./pages/Galeria'));
const Mensajes     = lazy(() => import('./pages/Mensajes'));
const Concurso     = lazy(() => import('./pages/Concurso'));
const Contacto     = lazy(() => import('./pages/Contacto'));
const Horarios     = lazy(() => import('./pages/Horarios'));
const Direcciones  = lazy(() => import('./pages/Direcciones'));
const DondeMeSiento = lazy(() => import('./pages/DondeMeSiento'));
const Admin        = lazy(() => import('./pages/Admin'));
const ManualImmich = lazy(() => import('./pages/ManualImmich'));

const RouteFallback = () => (
  <div
    style={{
      minHeight: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#5C5C5C',
      fontFamily: 'serif',
    }}
  >
    Cargando…
  </div>
);

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';
  // Carga fuentes personalizadas globalmente (inyecta @font-face en <head>).
  useFonts();

  // Aplica el título configurable de la pestaña del navegador
  useEffect(() => {
    let cancelled = false;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/public/config/textos`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        const t = (cfg.titulo_pestana || '').trim();
        if (t) document.title = t;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // v72 — auto-login por link único: si la URL tiene ?inv=XXX, identificamos al invitado
  // automáticamente y guardamos sesión web, sin pedir usuario/contraseña.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('inv');
    if (!tok || tok.length < 8) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/web/auth/por-token?inv=${encodeURIComponent(tok)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.ok || !data.invitado) return;
        // Sesión compatible con WebAuthGuard / ProtectedSectionGuard
        try {
          sessionStorage.setItem('webAuthenticated', 'true');
          sessionStorage.setItem('webAuthInvitado', JSON.stringify(data.invitado));
          sessionStorage.setItem('webAuthToken', tok);
          sessionStorage.setItem('webAuthVia', 'link');
        } catch (e) {}
        if (data.auto_limpiar_url) {
          // Limpia ?inv del URL para que no se quede en historial / capturas
          const url = new URL(window.location.href);
          url.searchParams.delete('inv');
          window.history.replaceState({}, '', url.toString());
        }
        // Forzar rerender suave (los componentes leen sessionStorage al montar)
        try { window.dispatchEvent(new Event('webauth:updated')); } catch (e) {}
      })
      .catch(() => {});
  }, []);

  return (
    <>
      {!isAdminPage && <Header />}
      {!isAdminPage && <MusicPlayer />}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Rutas públicas protegidas por WebAuthGuard */}
          <Route path="/" element={<WebAuthGuard><IntroGate><Home /></IntroGate></WebAuthGuard>} />
          <Route path="/confirmacion" element={<WebAuthGuard><ProtectedSectionGuard seccion="confirmacion"><Confirmacion /></ProtectedSectionGuard></WebAuthGuard>} />
          <Route path="/galeria" element={<WebAuthGuard><ProtectedSectionGuard seccion="galeria"><Galeria /></ProtectedSectionGuard></WebAuthGuard>} />
          <Route path="/galeria/fotos" element={<WebAuthGuard><ProtectedSectionGuard seccion="galeria"><Galeria /></ProtectedSectionGuard></WebAuthGuard>} />
          <Route path="/galeria/videos" element={<WebAuthGuard><ProtectedSectionGuard seccion="galeria"><Galeria /></ProtectedSectionGuard></WebAuthGuard>} />
          <Route path="/mensajes" element={<WebAuthGuard><ProtectedSectionGuard seccion="mensajes"><Mensajes /></ProtectedSectionGuard></WebAuthGuard>} />
          <Route path="/concurso" element={<WebAuthGuard><ProtectedSectionGuard seccion="concurso"><Concurso /></ProtectedSectionGuard></WebAuthGuard>} />
          <Route path="/contacto" element={<WebAuthGuard><Contacto /></WebAuthGuard>} />
          <Route path="/horarios" element={<WebAuthGuard><Horarios /></WebAuthGuard>} />
          <Route path="/direcciones" element={<WebAuthGuard><Direcciones /></WebAuthGuard>} />
          <Route path="/donde-me-siento" element={<WebAuthGuard><ProtectedSectionGuard seccion="mesas"><DondeMeSiento /></ProtectedSectionGuard></WebAuthGuard>} />
          {/* Admin sin protección de WebAuthGuard (tiene su propia autenticación) */}
          <Route path="/admin" element={<Admin />} />
          <Route path="/manual-immich" element={<ManualImmich />} />
        </Routes>
      </Suspense>
      <Toaster position="top-center" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
